import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { filter, flatMap, map, pick } from 'lodash/fp';
import { orderBy } from 'lodash';
import { CUSTOMER_FORM_ID, NEW_BANK } from '../../../../../global/constants';
import {
  saveSellOrderBanking,
  saveSellOrderOwnersAgreements,
  saveSellOrderRepresentatives,
} from '../../sell-process-redux';
import {
  isOrganizationOwner,
  isPersonOwner,
  mapApiRelationToStoreRelation,
} from '../../sell-process-utils';
import { SellPersonOwnerForm } from '../sell-process-form/components/owners-agreements/owners-agreements-types';
import * as SellProcessUtils from '../../sell-process-utils';
import { SellOrderRelation } from '../../sell-process-utils-types';
import { isPersonSeller } from '../sell-process-form/components/banking/banking-utils';
import { WithBank } from '../sell-process-form/components/banking/banking-types';
import { SellOrderDetailedApiResponse } from '../../../../../utils/sell-process/sell-process-detailed-types';
import { SellPersonEntity } from './sell-process-people-types';
import {
  personToPersonEntity,
  shouldInsertIntoBatch,
} from './sell-process-people-utils';

const sellProcessPeopleAdapter = createEntityAdapter<SellPersonEntity>({
  selectId: (model) => model.socialSecurityNumber,
});

export const sellProcessPeopleSelectors = sellProcessPeopleAdapter.getSelectors();

const selectById = (state: EntityState<SellPersonEntity>, id: string) =>
  sellProcessPeopleSelectors.selectById(state, id);

export const sellProcessPeople = createSlice({
  name: 'sellOrderOrganizations',
  initialState: {
    [CUSTOMER_FORM_ID]: {
      entities: {},
      ids: [],
    },
  } as Record<string, EntityState<SellPersonEntity>>,
  reducers: {
    initSellPeople(state, action: PayloadAction<{ id: string }>) {
      state[action.payload.id] = {
        entities: {},
        ids: [],
      };
    },

    saveSellOrderPeople(
      state,
      action: PayloadAction<{
        id: string;
        sellOrder: SellOrderDetailedApiResponse;
      }>,
    ) {
      const { sellOrder, id } = action.payload;
      const sellerOrganizations = SellProcessUtils.getSellerOrganizations(
        sellOrder.organizations,
      );

      const mapRelationToPerson = (relation: SellOrderRelation) => ({
        ...SellProcessUtils.pickSellPerson(relation),
        personId: relation.personId,
      });

      const peopleBatch = map(mapRelationToPerson, [
        ...(sellOrder.relations?.map(mapApiRelationToStoreRelation) ?? []),
        ...flatMap((x) => x.signers, sellerOrganizations),
      ]);

      upsertManyWithGuard(
        state[id],
        /**
         * Upsert works by inserting the first entity,
         * and then updating the *existing* properties
         * of that entity.
         *
         * This means that whenever the first person has
         * fewer properties than the second, but both have
         * the same social security number, the second person's
         * properties will be ignored in favor of the first
         * person's.
         *
         * Sorting by keys (descending) ensures that the entities with most
         * properties will be first, and the following ones will
         * simply update the existing, fewer properties and no
         * properties will be lost.
         * */
        orderBy(peopleBatch, (person) => Object.keys(person).length, [
          'desc',
        ]) as any,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveSellOrderOwnersAgreements, (state, action) => {
      const { sellers, id } = action.payload;
      const peopleSellers = sellers?.filter(isPersonOwner).map((seller) => {
        const { firstName, lastName } = SellProcessUtils.getNamesFromFullName(
            seller.fullName as string,
        );
        delete seller.fullName;
        return {
          ...seller,
          firstName,
          lastName,
        };
      }) ?? [];
      const organizationSellers = sellers?.filter(isOrganizationOwner) ?? [];

      const sellersBatch = filter((seller) => {
        const inStore = selectById(state[id], seller.socialSecurityNumber);
        return shouldInsertIntoBatch(personToPersonEntity, inStore, seller);
      }, peopleSellers);

      const signersBatch = filter(
        (signer: any) => {
          const inStore = selectById(state[id], signer.socialSecurityNumber);
          const { firstName, lastName } = SellProcessUtils.getNamesFromFullName(
            signer.fullName as string,
          );
          signer = {
            ...signer,
            firstName,
            lastName,
          };
          return shouldInsertIntoBatch(
            pick(['lastName', 'firstName', 'socialSecurityNumber']),
            inStore,
            signer,
          );
        },
        flatMap(
          (organization) => organization.signers ?? [],
          organizationSellers,
        ),
      );

      upsertManyWithGuard(
        state[id],
        map(personToPersonEntity, [...sellersBatch, ...signersBatch]),
      );
    });

    builder.addCase(saveSellOrderRepresentatives, (state, action) => {
      const { id, representatives } = action.payload;
      upsertManyWithGuard(
        state[id],
        map(personToPersonEntity, representatives),
      );
    });

    builder.addCase(saveSellOrderBanking, (state, action) => {
      const { id, sellers } = action.payload;

      const mapSeller = (seller: WithBank<SellPersonOwnerForm>) => {
        const isAnnanBankSelected = seller?.bankInfo?.id === NEW_BANK;
        return {
          // Need to be `null` specifically so that the backend knows to delete them
          bankId: isAnnanBankSelected ? null : seller?.bankInfo?.id ?? null,
          bankAccount: isAnnanBankSelected ? null : seller.bankAccount,
          socialSecurityNumber: seller.socialSecurityNumber,
        };
      };

      upsertManyWithGuard(
        state[id],
        // @ts-expect-error
        map(mapSeller, filter(isPersonSeller, sellers)),
      );
    });
  },
});

function upsertManyWithGuard(
  state: EntityState<SellPersonEntity>,
  entities: SellPersonEntity[],
) {
  for (const entity of entities) {
    if (entity.socialSecurityNumber) {
      sellProcessPeopleAdapter.upsertOne(state, entity);
    }
  }
}

export const sellOrderPeopleReducer = sellProcessPeople.reducer;
export const { initSellPeople, saveSellOrderPeople } = sellProcessPeople.actions;
